import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useUrlState } from "@/lib/useUrlState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  ClipboardText, Plus, Lightning, MagnifyingGlass, X,
  CheckCircle, ArrowCounterClockwise, Warning, PencilSimple, Trash, Clock,
  Phone, Buildings,
} from "@phosphor-icons/react";
import CallLogDialog from "@/components/CallLogDialog";
import TaskFormDialog from "@/components/TaskFormDialog";
import { useNavCounts } from "@/contexts/NavCountsContext";

const STATUS_STYLES = {
  pending:  "bg-amber-50 border-amber-200 text-amber-700",
  stuck:    "bg-rose-50 border-rose-200 text-rose-700",
  done:     "bg-emerald-50 border-emerald-200 text-emerald-700",
};

const URGENT_PILL = "bg-rose-100 text-rose-700 border-rose-200";

// Pretty formatter for ISO timestamps (en-IN, 12h clock)
function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) { return iso; }
}

export default function PendingTasks() {
  const { user: me } = useAuth();
  const { refresh: refreshNavCounts } = useNavCounts();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  const isAdminPlus = me?.role === "admin" || me?.role === "super_admin";

  // URL state for filters
  const [tab, setTab] = useUrlState("tab", "pending");
  const [assignee, setAssignee] = useUrlState("assignee", "all");
  const [assigner, setAssigner] = useUrlState("assigner", "all");
  const [statusFilter, setStatusFilter] = useUrlState("status", "all");  // pending | blocked
  const [urgentOnly, setUrgentOnly] = useUrlState("urgent", "");          // "1" | ""
  const [dueFrom, setDueFrom] = useUrlState("due_from", "");
  const [dueTo, setDueTo] = useUrlState("due_to", "");
  const [overdueOnly, setOverdueOnly] = useUrlState("overdue", "");
  const [search, setSearch] = useUrlState("q", "");
  const [firmFilter, setFirmFilter] = useUrlState("firm", "all");
  const [viewMode, setViewMode] = useUrlState("view", "assigned_to_me");

  const focusId = searchParams.get("focus");
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);     // task object being edited
  const [doneFor, setDoneFor] = useState(null);     // task object for mark-done dialog
  const [stuckFor, setStuckFor] = useState(null);   // task object for Stuck history dialog
  const [logCallFor, setLogCallFor] = useState(null); // {task, remark} for "Mark done & log call"
  const [firmOptions, setFirmOptions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        status: tab === "completed" ? "completed" : "pending",
        page: 1, page_size: 200,
        view_mode: viewMode,
      };
      if (assignee !== "all") params.assignee = assignee;
      if (assigner !== "all") params.assigner = assigner;
      if (statusFilter !== "all" && tab !== "completed") params.status_filter = statusFilter;
      if (urgentOnly) params.urgent_only = true;
      if (overdueOnly && tab !== "completed") params.overdue_only = true;
      if (dueFrom) params.due_from = dueFrom;
      if (dueTo) params.due_to = dueTo;
      if (search) params.search = search;
      if (firmFilter !== "all") params.firm = firmFilter;
      const { data } = await api.get("/tasks", { params });
      setRows(data?.rows || []);
      // Any list refresh (which follows every mutation) also refreshes the
      // sidebar badge, so counts stay in lockstep without per-action wiring.
      refreshNavCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, assignee, assigner, statusFilter, urgentOnly, dueFrom, dueTo, overdueOnly, search, firmFilter, viewMode]);

  useEffect(() => {
    api.get("/tasks/users").then(r => setUsers(r.data?.users || [])).catch(() => {});
    api.get("/call-logs/firm-options").then(r => setFirmOptions(r.data?.firms || [])).catch(() => {});
  }, []);

  // Clear assignee filter when switching to "assigned_to_me" mode
  useEffect(() => {
    if (viewMode === "assigned_to_me" && assignee !== "all") {
      setAssignee("all");
    }
  }, [viewMode, assignee, setAssignee]);

  // Clear assigner filter when switching to "created_by_me" mode
  useEffect(() => {
    if (viewMode === "created_by_me" && assigner !== "all") {
      setAssigner("all");
    }
  }, [viewMode, assigner, setAssigner]);

  const clearAll = () => setSearchParams({ tab }, { replace: true });

  const filtersDirty = (
    assignee !== "all" || assigner !== "all" || statusFilter !== "all" ||
    !!urgentOnly || !!dueFrom || !!dueTo || !!overdueOnly || !!search ||
    firmFilter !== "all"
  );

  const today = new Date().toISOString().slice(0, 10);
  const overdueOf = (r) => r.due_date && r.due_date < today && r.status !== "done";

  // Auto-scroll to focused row from /tasks?focus=<id>
  useEffect(() => {
    if (!focusId || rows.length === 0) return;
    const el = document.querySelector(`[data-task-id="${focusId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusId, rows]);

  return (
    <div className="space-y-5" data-testid="pending-tasks-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <ClipboardText size={22} className="text-violet-600" />
            Pending Tasks
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Assign work, track follow-ups, never lose a task again.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-[180px] h-9" data-testid="view-mode-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="assigned_to_me">Assigned to Me</SelectItem>
              <SelectItem value="created_by_me">Created by Me</SelectItem>
              {isAdminPlus && <SelectItem value="all">All Tasks</SelectItem>}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowCreate(true)} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="create-task-btn">
            <Plus size={14} className="mr-1.5" /> New task
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex items-center gap-1">
          {[["pending", "Pending"], ["completed", "Completed"]].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setTab(v)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                tab === v ? "border-violet-600 text-violet-700" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`tab-${v}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-md border border-gray-200 bg-white p-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title…" className="h-9 text-xs pl-7 w-[180px]" data-testid="task-search" />
        </div>
        {/* Hide assignee filter when viewing "Assigned to Me" */}
        {viewMode !== "assigned_to_me" && (
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="task-assignee-filter"><SelectValue placeholder="Assignee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assignees</SelectItem>
              {users.map((u) => <SelectItem key={`a-${u.email}`} value={u.email}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {/* Hide assigner filter when viewing "Created by Me" */}
        {viewMode !== "created_by_me" && (
          <Select value={assigner} onValueChange={setAssigner}>
            <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="task-assigner-filter"><SelectValue placeholder="Assigner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assigners</SelectItem>
              {users.map((u) => <SelectItem key={`b-${u.email}`} value={u.email}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {tab === "pending" ? (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="task-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="stuck">Stuck</SelectItem>
            </SelectContent>
          </Select>
        ) : null}
        <Select value={firmFilter} onValueChange={setFirmFilter}>
          <SelectTrigger className="w-[180px] h-9 text-xs" data-testid="task-firm-filter">
            <SelectValue placeholder="Firm" />
          </SelectTrigger>
          <SelectContent className="max-h-[320px]">
            <SelectItem value="all">All firms</SelectItem>
            {firmOptions.map((f) => (
              <SelectItem key={`firm-${f.subdomain}`} value={f.subdomain}>{f.firm_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="date" value={dueFrom} onChange={(e) => setDueFrom(e.target.value)} className="h-9 text-xs w-[140px]" data-testid="task-due-from" />
        <span className="text-gray-400 text-xs">→</span>
        <Input type="date" value={dueTo} onChange={(e) => setDueTo(e.target.value)} className="h-9 text-xs w-[140px]" data-testid="task-due-to" />
        <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer ml-2">
          <Switch checked={!!urgentOnly} onCheckedChange={(v) => setUrgentOnly(v ? "1" : "")} data-testid="task-urgent-toggle" />
          Urgent only
        </label>
        {tab === "pending" ? (
          <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
            <Switch checked={!!overdueOnly} onCheckedChange={(v) => setOverdueOnly(v ? "1" : "")} data-testid="task-overdue-toggle" />
            Overdue only
          </label>
        ) : null}
        {filtersDirty ? (
          <Button onClick={clearAll} variant="ghost" size="sm" className="text-xs text-gray-500 h-9 ml-auto" data-testid="task-clear-filters">
            <X size={12} className="mr-1" /> Clear
          </Button>
        ) : null}
      </div>

      {/* List */}
      <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-gray-400">
            {tab === "completed" ? "No completed tasks here yet." : "Nothing pending. Nice."}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((r) => {
              const isOverdue = overdueOf(r);
              const isAssignee = me?.email === r.assignee_email;
              const isAssigner = me?.email === r.assigner_email;
              const canEdit = isAssigner && tab === "pending";
              const canActOnStatus = isAssignee || isAssigner;
              return (
                <li
                  key={r.task_id}
                  data-task-id={r.task_id}
                  className={`px-4 py-3 hover:bg-gray-50 transition-colors ${focusId === r.task_id ? "bg-violet-50" : ""}`}
                  data-testid={`task-row-${r.task_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {r.is_urgent ? (
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${URGENT_PILL}`}>
                            <Lightning size={10} weight="fill" /> Urgent
                          </span>
                        ) : null}
                        <span className="font-medium text-gray-900">{r.title}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${STATUS_STYLES[r.status]}`}>
                          {r.status}
                        </span>
                        {r.firm_name ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-violet-50 border-violet-200 text-violet-700"
                            data-testid={`task-firm-chip-${r.task_id}`}
                            title={r.firm_subdomain}
                          >
                            <Buildings size={10} weight="bold" /> {r.firm_name}
                          </span>
                        ) : null}
                        {r.trial_firm_name ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-blue-50 border-blue-200 text-blue-700"
                            data-testid={`task-trial-chip-${r.task_id}`}
                            title={r.trial_firm_url}
                          >
                            <Buildings size={10} weight="bold" /> {r.trial_firm_name}
                          </span>
                        ) : null}
                        {isOverdue ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                            <Warning size={10} /> Overdue
                          </span>
                        ) : null}
                      </div>
                      {r.description ? (
                        <DescriptionClamp text={r.description} testId={r.task_id} />
                      ) : null}
                      <StuckSummary row={r} onOpen={() => setStuckFor(r)} />
                      {r.status === "done" && r.completion_remark ? (
                        <div className="mt-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                          ✓ {r.completion_remark}
                        </div>
                      ) : null}
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                        <span>By <b className="text-gray-700">{r.assigner_name}</b> → <b className="text-gray-700">{r.assignee_name}</b></span>
                        {r.due_date ? (
                          <span className={`inline-flex items-center gap-1 ${isOverdue ? "text-rose-600 font-semibold" : ""}`}>
                            <Clock size={11} /> Due {r.due_date}
                          </span>
                        ) : (
                          <span className="text-gray-400">No due date</span>
                        )}
                        {r.reopened_count ? (
                          <span className="text-violet-600">↻ reopened ×{r.reopened_count}</span>
                        ) : null}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-[10px] text-gray-400 tabular-nums">
                        <span>Created {fmtDateTime(r.created_at)}</span>
                        {r.status === "done" && r.completed_at ? (
                          <span>
                            · Completed {fmtDateTime(r.completed_at)}
                            {r.completed_by_name ? <> · by <span className="text-gray-500">{r.completed_by_name}</span></> : null}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="shrink-0 flex flex-col items-end gap-1.5">
                      {tab === "pending" ? (
                        <div className="flex items-center gap-1">
                          {canActOnStatus && r.status !== "done" ? (
                            <Button onClick={() => setDoneFor(r)} size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`mark-done-${r.task_id}`}>
                              <CheckCircle size={12} className="mr-1" /> Done
                            </Button>
                          ) : null}
                          {canActOnStatus && r.status !== "done" && (r.firm_subdomain || r.trial_firm_name) ? (
                            <Button
                              onClick={() => setLogCallFor({ task: r, remark: "" })}
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              data-testid={`done-and-log-${r.task_id}`}
                              title="Mark done and log a call against this firm"
                            >
                              <Phone size={12} className="mr-1" /> Done &amp; log call
                            </Button>
                          ) : null}
                          {isAssignee && r.status !== "done" ? (
                            <Button onClick={() => setStuckFor(r)} size="sm" variant="outline" className="h-7 text-xs" data-testid={`stuck-${r.task_id}`}>
                              <Warning size={12} className="mr-1" /> {r.status === "stuck" ? "Stuck" : "Stuck"}
                            </Button>
                          ) : null}
                          {canEdit ? (
                            <Button onClick={() => setEditing(r)} size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`edit-${r.task_id}`}>
                              <PencilSimple size={12} />
                            </Button>
                          ) : null}
                          {canEdit ? (
                            <Button
                              onClick={async () => {
                                const ok = await confirm({ title: "Delete task?", message: "This will permanently remove the task.", tone: "danger", confirmLabel: "Delete" });
                                if (!ok) return;
                                try {
                                  await api.delete(`/tasks/${r.task_id}`);
                                  toast.success("Deleted");
                                  load();
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Delete failed");
                                }
                              }}
                              size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" data-testid={`delete-${r.task_id}`}
                            >
                              <Trash size={12} />
                            </Button>
                          ) : null}
                        </div>
                      ) : canActOnStatus ? (
                        <Button
                          onClick={async () => {
                            try {
                              await api.post(`/tasks/${r.task_id}/reopen`, { reason: "" });
                              toast.success("Task reopened");
                              load();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Failed");
                            }
                          }}
                          size="sm" variant="outline" className="h-7 text-xs"
                          data-testid={`reopen-${r.task_id}`}
                        >
                          <ArrowCounterClockwise size={12} className="mr-1" /> Reopen
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Dialogs */}
      <TaskFormDialog
        open={showCreate || !!editing}
        editing={editing}
        users={users}
        onClose={() => { setShowCreate(false); setEditing(null); }}
        onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
      />
      <RemarkDialog
        open={!!doneFor}
        title="Mark task as done"
        label="Completion remark"
        placeholder="What did you do? Any outcome notes…"
        confirmLabel="Mark done"
        tone="emerald"
        onClose={() => setDoneFor(null)}
        onSubmit={async (text) => {
          try {
            await api.post(`/tasks/${doneFor.task_id}/mark-done`, { completion_remark: text });
            toast.success("Task marked done");
            setDoneFor(null); load();
          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        }}
      />
      <StuckHistoryDialog
        task={stuckFor}
        currentEmail={me?.email}
        onClose={() => setStuckFor(null)}
        onChanged={load}
      />
      {/* Combined "Mark done & log call" flow:
          - Open the CallLogDialog with the task's firm pinned (either live or trial).
          - On successful save we receive the remark from CallLogDialog and use
            it as the completion_remark for /tasks/.../mark-done. */}
      <CallLogDialog
        open={!!logCallFor}
        lockedFirm={logCallFor && logCallFor.task.firm_subdomain ? 
          { subdomain: logCallFor.task.firm_subdomain, firm_name: logCallFor.task.firm_name } : 
          null}
        lockedTrial={logCallFor && logCallFor.task.trial_firm_name ?
          { name: logCallFor.task.trial_firm_name, url: logCallFor.task.trial_firm_url } :
          null}
        lockedType="call"
        defaultRemark=""
        onOpenChange={(v) => { if (!v) setLogCallFor(null); }}
        onSaved={async ({ remark } = {}) => {
          const task = logCallFor?.task;
          if (!task) return;
          try {
            await api.post(`/tasks/${task.task_id}/mark-done`, {
              completion_remark: (remark && remark.trim()) || "Logged a call",
            });
            toast.success("Call logged · task marked done");
          } catch (e) {
            toast.error("Call logged but task mark-done failed: " + (e?.response?.data?.detail || ""));
          } finally {
            setLogCallFor(null);
            load();
          }
        }}
      />
    </div>
  );
}

// ---------- Description with 4-line clamp + show more/less ----------
// Long descriptions used to dominate the row; this clamps to 4 lines and
// shows an inline toggle only when there's actually hidden content.
function DescriptionClamp({ text, testId }) {
  const [expanded, setExpanded] = useState(false);
  const [needsToggle, setNeedsToggle] = useState(false);
  const ref = useRef(null);

  useLayoutEffect(() => {
    // Measure only while clamped; once we've decided a toggle is needed,
    // keep it visible so expand/collapse doesn't flicker the button.
    if (expanded || !ref.current) return;
    const el = ref.current;
    if (el.scrollHeight > el.clientHeight + 1) setNeedsToggle(true);
  }, [text, expanded]);

  return (
    <div className="mt-1" onClick={(e) => e.stopPropagation()}>
      <div
        ref={ref}
        className={`text-xs text-gray-600 whitespace-pre-line ${expanded ? "" : "line-clamp-4"}`}
        data-testid={testId ? `task-desc-${testId}` : undefined}
      >
        {text}
      </div>
      {needsToggle && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 text-[11px] font-medium text-violet-700 hover:text-violet-900"
          data-testid={testId ? `task-desc-toggle-${testId}` : undefined}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}


// ---------- Compact stuck summary (inline, single-line) ----------
function StuckSummary({ row, onOpen }) {
  const log = Array.isArray(row.status_log) ? row.status_log : [];
  const stuckEntries = log.filter((e) => e.kind === "stuck");
  // Legacy fallback for rows pre-migration
  if (stuckEntries.length === 0 && row.status === "stuck" && row.status_note) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="mt-1.5 w-full text-left text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded px-2 py-1 hover:bg-rose-100 transition-colors"
        data-testid={`stuck-summary-${row.task_id}`}
      >
        <span className="font-medium">⚠ Stuck:</span> {row.status_note}
      </button>
    );
  }
  if (stuckEntries.length === 0) return null;

  const latest = stuckEntries[stuckEntries.length - 1];
  const moreCount = stuckEntries.length - 1;
  const isCurrentlyStuck = row.status === "stuck";
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`mt-1.5 w-full text-left text-xs rounded px-2 py-1 border transition-colors ${
        isCurrentlyStuck
          ? "text-rose-700 bg-rose-50 border-rose-100 hover:bg-rose-100"
          : "text-gray-600 bg-gray-50 border-gray-100 hover:bg-gray-100"
      }`}
      data-testid={`stuck-summary-${row.task_id}`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${isCurrentlyStuck ? "bg-rose-500" : "bg-gray-400"}`} aria-hidden />
        <span className="font-medium">{isCurrentlyStuck ? "Stuck:" : "Was stuck:"}</span>
        <span className="truncate flex-1">{latest.note}</span>
        {moreCount > 0 ? (
          <span className="shrink-0 text-[10px] text-gray-500">+{moreCount} more</span>
        ) : null}
      </div>
    </button>
  );
}

// ---------- Stuck history dialog (add, edit, resume) ----------
function StuckHistoryDialog({ task, currentEmail, onClose, onChanged }) {
  const open = !!task;
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft("");
      setEditingId(null);
      setEditingText("");
    }
  }, [open, task?.task_id]);

  if (!task) return null;
  const entries = (task.status_log || []).filter((e) => e.kind === "stuck");
  const isCurrentlyStuck = task.status === "stuck";
  const canResume = isCurrentlyStuck && (currentEmail === task.assignee_email || currentEmail === task.assigner_email);
  const isAssignee = currentEmail === task.assignee_email;

  const submitNew = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${task.task_id}/stuck`, { status_note: text });
      toast.success(isCurrentlyStuck ? "New stuck note added" : "Task flagged as stuck");
      setDraft("");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const saveEdit = async (entryId) => {
    const text = editingText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.task_id}/stuck-entry/${entryId}`, { note: text });
      toast.success("Note updated");
      setEditingId(null); setEditingText("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task.task_id}/resume`);
      toast.success("Task resumed");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="stuck-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warning size={18} className="text-rose-600" />
            Stuck history — <span className="font-normal text-gray-700">{task.title}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Add new */}
        {isAssignee ? (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2" data-testid="add-stuck-card">
            <Label className="text-xs font-medium text-gray-700">
              {isCurrentlyStuck ? "Add another stuck note" : "Flag as stuck"}
            </Label>
            <Textarea
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="e.g. Waiting on client to share GST number…"
              data-testid="new-stuck-textarea"
            />
            <div className="flex justify-end">
              <Button
                onClick={submitNew}
                disabled={!draft.trim() || busy}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="add-stuck-submit"
              >
                {isCurrentlyStuck ? "Add note" : "Flag stuck"}
              </Button>
            </div>
          </div>
        ) : null}

        {/* History */}
        <div className="mt-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
            History {entries.length > 0 ? `(${entries.length})` : ""}
          </div>
          {entries.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-6">No stuck notes yet.</div>
          ) : (
            <ol className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
              {[...entries].reverse().map((e) => {
                const canEditEntry = currentEmail === e.by_email;
                const isEditing = editingId === e.entry_id;
                return (
                  <li
                    key={e.entry_id}
                    className="rounded border border-gray-200 bg-white px-3 py-2"
                    data-testid={`stuck-entry-${e.entry_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[11px] text-gray-500 tabular-nums">
                        {fmtDateTime(e.at)} · {e.by_name}
                        {e.edited_at ? <span className="text-gray-400"> · edited {fmtDateTime(e.edited_at)}</span> : null}
                      </div>
                      {canEditEntry && !isEditing ? (
                        <button
                          type="button"
                          onClick={() => { setEditingId(e.entry_id); setEditingText(e.note || ""); }}
                          className="text-[11px] text-violet-700 hover:underline shrink-0"
                          data-testid={`edit-stuck-${e.entry_id}`}
                        >
                          Edit
                        </button>
                      ) : null}
                    </div>
                    {isEditing ? (
                      <div className="mt-1.5 space-y-2">
                        <Textarea
                          rows={3}
                          value={editingText}
                          onChange={(ev) => setEditingText(ev.target.value)}
                          data-testid={`edit-stuck-textarea-${e.entry_id}`}
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setEditingId(null); setEditingText(""); }} disabled={busy}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => saveEdit(e.entry_id)}
                            disabled={!editingText.trim() || busy}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                            data-testid={`save-stuck-${e.entry_id}`}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-1 text-sm text-gray-800 whitespace-pre-line">{e.note}</div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div>
            {canResume ? (
              <Button
                variant="outline"
                onClick={resume}
                disabled={busy}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="resume-task-btn"
              >
                <ArrowCounterClockwise size={14} className="mr-1.5" /> Resume task
              </Button>
            ) : null}
          </div>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Generic remark dialog ----------
function RemarkDialog({ open, title, label, placeholder, confirmLabel, tone = "violet", onClose, onSubmit }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  const toneClasses = {
    emerald: "bg-emerald-600 hover:bg-emerald-700",
    rose:    "bg-rose-600 hover:bg-rose-700",
    violet:  "bg-violet-600 hover:bg-violet-700",
  }[tone];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="task-remark-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div>
          <Label>{label}</Label>
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className="mt-1" data-testid="task-remark-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit(text.trim())} disabled={text.trim().length === 0} className={`${toneClasses} text-white`} data-testid="task-remark-submit">
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
